import React, { useState, useEffect } from 'react';
import { 
  Grid, Paper, Typography, Card, CardHeader, CardContent, 
  Chip, Box, Button, Alert, CircularProgress, Divider 
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../../../services/auth';
import { ROUTES } from '../../../routes';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ArticleIcon from '@mui/icons-material/Article';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import GavelIcon from '@mui/icons-material/Gavel';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import axios from 'axios';
import { addAuthHeader } from '../../../utils/auth';

// Styled components
const DashboardContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: '#F5F5F5',
  minHeight: '100vh',
}));

const DashboardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: theme.spacing(4),
}));

const DashboardTitle = styled(Typography)(({ theme }) => ({
  color: '#064d51',
  fontWeight: 600,
  marginBottom: theme.spacing(1),
}));

const DashboardSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
}));

const DashboardCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  transition: 'transform 0.2s, box-shadow 0.2s',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
}));

const IconBox = styled(Box)(({ theme, color }) => ({
  backgroundColor: color,
  color: 'white',
  borderRadius: '50%',
  width: '48px',
  height: '48px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
}));

const MetricValue = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 600,
  color: '#064d51',
  marginBottom: theme.spacing(1),
}));

const MetricLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency: 'AED'
  }).format(amount);
};

// Check if we're in development mode
const isDevelopmentMode = true; // Force development mode for now

// Mock data for development
const mockDashboardData = {
  organizationName: 'Customer Organization 3',
  activeRequests: 12,
  pendingDocuments: 8,
  walletBalance: 25000,
  expiringDocuments: 3,
  recentActivity: [
    { id: 1, type: 'Document Upload', description: 'New employee documents uploaded', date: '2025-05-24', status: 'Completed' },
    { id: 2, type: 'Wallet Topup', description: 'Wallet credited with 10,000 AED', date: '2025-05-23', status: 'Completed' },
    { id: 3, type: 'New Request', description: 'Outside visa requested for John Smith', date: '2025-05-22', status: 'In Progress' },
    { id: 4, type: 'Document Renewal', description: 'Company license renewal initiated', date: '2025-05-21', status: 'Pending Approval' },
  ],
  dashboards: [
    { title: 'Document Services', description: 'View and manage document services and status', icon: 'description', path: '/CST-Dashboard' },
    { title: 'Financial', description: 'Track expenses, invoices, and wallet balance', icon: 'account_balance_wallet', path: '/CST-Dashboard/financial' },
    { title: 'Legal', description: 'Monitor legal services and compliance', icon: 'gavel', path: '/CST-Dashboard/legal' },
    { title: 'Training', description: 'Access training programs and certificates', icon: 'school', path: '/CST-Dashboard/training' },
    { title: 'Workforce', description: 'Manage employees and their services', icon: 'people', path: '/workforce' },
  ],
  quickLinks: [
    { title: 'Create New Request', icon: 'add_circle', path: '/workflow/add' },
    { title: 'Document Status', icon: 'find_in_page', path: '/documents/status' },
    { title: 'Top Up Wallet', icon: 'account_balance_wallet', path: '/wallet/topup' },
    { title: 'Contact Support', icon: 'support_agent', path: '/support' },
  ]
};

// API to fetch dashboard overview data
const fetchDashboardOverview = async () => {
  if (isDevelopmentMode) {
    console.log('[CustomerAdminDashboard] Development mode - returning mock data');
    return mockDashboardData;
  }
  
  try {
    console.log('[CustomerAdminDashboard] Fetching dashboard overview data...');
    const response = await axios.get('/api/customer/dashboard/overview', addAuthHeader());
    console.log('[CustomerAdminDashboard] Dashboard data fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('[CustomerAdminDashboard] Error fetching dashboard overview:', error);
    // Fallback data for development
    return {
      organizationName: 'Al Futtaim Group',
      activeRequests: 42,
      pendingDocuments: 15,
      walletBalance: 15420.50,
      expiringDocuments: 8,
      activeEmployees: 156,
      totalInvoices: 22,
      pendingInvoices: 4
    };
  }
};

// Main Dashboard Component
const CustomerAdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [user, setUser] = useState(null);

  // Check authorization and load data
  useEffect(() => {
    console.log('[CustomerAdminDashboard] Component mounted');
    
    const loadDashboardData = async () => {
      try {
        console.log('[CustomerAdminDashboard] Loading dashboard data...');
        setLoading(true);
        
        if (isDevelopmentMode) {
          // In development mode, bypass authentication completely
          console.log('[CustomerAdminDashboard] Running in DEVELOPMENT mode - using mock data');

          // Create org-specific mock data if needed
          let customizedMockData = {...mockDashboardData};

          // Check if a specific organization ID is set in localStorage or URL params
          const urlParams = new URLSearchParams(window.location.search);
          const orgIdFromUrl = urlParams.get('orgId');
          const orgIdFromStorage = localStorage.getItem('selectedOrganizationId');
          const orgId = orgIdFromUrl || orgIdFromStorage;

          console.log('[CustomerAdminDashboard] Organization ID detected:', orgId);

          // If org-customer-3 is detected, customize mock data
          if (orgId === 'org-customer-3' || window.location.href.includes('org-customer-3')) {
            console.log('[CustomerAdminDashboard] Customizing mock data for org-customer-3');
            customizedMockData.organizationName = 'Customer Organization 3';
            customizedMockData.walletBalance = 35000;
            customizedMockData.activeRequests = 15;
          }

          setDashboardData(customizedMockData);
          setAuthorized(true);
          setError(null);
          setLoading(false);
          return;
        }
        
        // Production mode - check authorization
        console.log('[CustomerAdminDashboard] Fetching current user information...');
        const currentUser = await getCurrentUser();
        console.log('[CustomerAdminDashboard] Current user:', currentUser);
        setUser(currentUser);
        
        if (currentUser && 
            currentUser.organization && 
            currentUser.organization.type === 'CUSTOMER' && 
            currentUser.roles.some(role => role.includes('ADMIN'))) {
          
          console.log('[CustomerAdminDashboard] User is authorized (customer organization admin)');
          console.log('[CustomerAdminDashboard] Organization:', currentUser.organization);
          console.log('[CustomerAdminDashboard] Roles:', currentUser.roles);
          setAuthorized(true);
          
          // Load dashboard data
          console.log('[CustomerAdminDashboard] Loading dashboard data...');
          const overview = await fetchDashboardOverview();
          console.log('[CustomerAdminDashboard] Dashboard data loaded:', overview);
          setDashboardData(overview);
          setError(null);
        } else {
          console.warn('[CustomerAdminDashboard] User not authorized to view this dashboard');
          console.log('[CustomerAdminDashboard] User details:', {
            hasUser: !!currentUser,
            hasOrganization: !!(currentUser && currentUser.organization),
            organizationType: currentUser?.organization?.type,
            roles: currentUser?.roles
          });
          setAuthorized(false);
          setError('Access to this dashboard is restricted to Customer Organization Administrators.');
        }
      } catch (err) {
        console.error('[CustomerAdminDashboard] Error loading dashboard:', err);
        console.error('[CustomerAdminDashboard] Error details:', err.response?.data || err.message);
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        console.log('[CustomerAdminDashboard] Finished loading. Setting loading state to false');
        setLoading(false);
      }
    };
    
    loadDashboardData();
  }, [navigate]);
  
  // Navigate to specific dashboards
  const navigateToDashboard = (path) => {
    console.log(`[CustomerAdminDashboard] Navigating to: ${path}`);
    navigate(path);
  };
  
  // Loading state
  if (loading) {
    console.log('[CustomerAdminDashboard] Rendering loading state');
    return (
      <DashboardContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress size={40} sx={{ color: '#064d51' }} />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading dashboard...
          </Typography>
        </Box>
      </DashboardContainer>
    );
  }
  
  // Unauthorized access
  if (!authorized && !isDevelopmentMode) {
    console.log('[CustomerAdminDashboard] Rendering unauthorized state');
    return (
      <DashboardContainer>
        <Box sx={{ maxWidth: '800px', mx: 'auto', mt: 4 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body1">
              {error || 'Access to this dashboard is restricted to Customer Organization Administrators.'}
            </Typography>
          </Alert>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => navigate('/')}
            sx={{ mt: 2 }}
          >
            Return to Home
          </Button>
        </Box>
      </DashboardContainer>
    );
  }
  
  // No data available
  if (!dashboardData) {
    console.log('[CustomerAdminDashboard] Rendering no data state');
    return (
      <DashboardContainer>
        <Box sx={{ maxWidth: '800px', mx: 'auto', mt: 4 }}>
          <Alert severity="info">
            <Typography variant="body1">
              No dashboard data available. Please try again later.
            </Typography>
          </Alert>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => window.location.reload()}
            sx={{ mt: 2 }}
          >
            Refresh Dashboard
          </Button>
        </Box>
      </DashboardContainer>
    );
  }
  
  console.log('[CustomerAdminDashboard] Rendering dashboard with data:', dashboardData);
  return (
    <DashboardContainer>
      {isDevelopmentMode && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Development Mode:</strong> Authentication has been bypassed. In production, this dashboard is restricted to Customer Organization Administrators.
          </Typography>
        </Alert>
      )}
      <DashboardHeader>
        <DashboardTitle variant="h4">
          {dashboardData.organizationName} Dashboard
        </DashboardTitle>
        <Typography variant="subtitle1" color="textSecondary">
          Overview of your organization's services and status
        </Typography>
      </DashboardHeader>
      
      {/* Key metrics overview */}
      <DashboardSection>
        <Typography variant="h6" sx={{ mb: 2, color: '#064d51' }}>
          Key Metrics
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} sx={{ p: 2, textAlign: 'center', backgroundColor: 'rgba(6, 77, 81, 0.05)' }}>
              <MetricValue>{dashboardData.activeRequests}</MetricValue>
              <MetricLabel>Active Requests</MetricLabel>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} sx={{ p: 2, textAlign: 'center', backgroundColor: 'rgba(243, 156, 18, 0.05)' }}>
              <MetricValue>{dashboardData.pendingDocuments}</MetricValue>
              <MetricLabel>Pending Documents</MetricLabel>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} sx={{ p: 2, textAlign: 'center', backgroundColor: 'rgba(39, 174, 96, 0.05)' }}>
              <MetricValue>{formatCurrency(dashboardData.walletBalance)}</MetricValue>
              <MetricLabel>Wallet Balance</MetricLabel>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} sx={{ p: 2, textAlign: 'center', backgroundColor: 'rgba(231, 76, 60, 0.05)' }}>
              <MetricValue>{dashboardData.expiringDocuments}</MetricValue>
              <MetricLabel>Expiring Documents</MetricLabel>
            </Paper>
          </Grid>
        </Grid>
      </DashboardSection>
      
      {/* Dashboard navigation cards */}
      <DashboardSection>
        <Typography variant="h6" sx={{ mb: 2, color: '#064d51' }}>
          Service Dashboards
        </Typography>
        <Grid container spacing={3}>
          {/* Document Services Dashboard */}
          <Grid item xs={12} sm={6} md={4}>
            <DashboardCard onClick={() => navigateToDashboard(ROUTES.CUSTOMER.DASHBOARD.DOCUMENTS)}>
              <Box>
                <IconBox color="#064d51">
                  <ArticleIcon />
                </IconBox>
                <Typography variant="h6" sx={{ mb: 1 }}>Document Services</Typography>
                <Typography variant="body2" color="textSecondary">
                  Track document processing, expirations, and compliance status for your organization.
                </Typography>
              </Box>
              <Button 
                variant="outlined" 
                size="small" 
                sx={{ mt: 2, alignSelf: 'flex-start' }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigateToDashboard(ROUTES.CUSTOMER.DASHBOARD.DOCUMENTS);
                }}
              >
                View Dashboard
              </Button>
            </DashboardCard>
          </Grid>
          
          {/* Financial Dashboard */}
          <Grid item xs={12} sm={6} md={4}>
            <DashboardCard onClick={() => navigateToDashboard(ROUTES.CUSTOMER.DASHBOARD.FINANCIAL)}>
              <Box>
                <IconBox color="#27ae60">
                  <AccountBalanceWalletIcon />
                </IconBox>
                <Typography variant="h6" sx={{ mb: 1 }}>Financial Dashboard</Typography>
                <Typography variant="body2" color="textSecondary">
                  Manage wallet balance, track invoices, and analyze expenses by service category.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateToDashboard(ROUTES.CUSTOMER.DASHBOARD.FINANCIAL);
                  }}
                >
                  View Dashboard
                </Button>
                <Chip 
                  label={`${dashboardData.pendingInvoices} pending`} 
                  size="small" 
                  color="primary"
                  sx={{ backgroundColor: dashboardData.pendingInvoices > 0 ? '#f39c12' : '#27ae60' }}
                />
              </Box>
            </DashboardCard>
          </Grid>
          
          {/* Legal Dashboard */}
          <Grid item xs={12} sm={6} md={4}>
            <DashboardCard onClick={() => navigateToDashboard(ROUTES.CUSTOMER.DASHBOARD.LEGAL)}>
              <Box>
                <IconBox color="#3498db">
                  <GavelIcon />
                </IconBox>
                <Typography variant="h6" sx={{ mb: 1 }}>Legal Support</Typography>
                <Typography variant="body2" color="textSecondary">
                  Monitor legal case management, document tracking, and legal partner performance.
                </Typography>
              </Box>
              <Button 
                variant="outlined" 
                size="small" 
                sx={{ mt: 2, alignSelf: 'flex-start' }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigateToDashboard(ROUTES.CUSTOMER.DASHBOARD.LEGAL);
                }}
              >
                View Dashboard
              </Button>
            </DashboardCard>
          </Grid>
          
          {/* Training Dashboard */}
          <Grid item xs={12} sm={6} md={4}>
            <DashboardCard onClick={() => navigateToDashboard(ROUTES.CUSTOMER.DASHBOARD.TRAINING)}>
              <Box>
                <IconBox color="#9b59b6">
                  <SchoolIcon />
                </IconBox>
                <Typography variant="h6" sx={{ mb: 1 }}>Training Programs</Typography>
                <Typography variant="body2" color="textSecondary">
                  Track training program metrics, enrollment status, and certification management.
                </Typography>
              </Box>
              <Button 
                variant="outlined" 
                size="small" 
                sx={{ mt: 2, alignSelf: 'flex-start' }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigateToDashboard(ROUTES.CUSTOMER.DASHBOARD.TRAINING);
                }}
              >
                View Dashboard
              </Button>
            </DashboardCard>
          </Grid>
          
          {/* Workforce Dashboard */}
          <Grid item xs={12} sm={6} md={4}>
            <DashboardCard onClick={() => navigateToDashboard(ROUTES.CUSTOMER.EMPLOYEES.LIST)}>
              <Box>
                <IconBox color="#e67e22">
                  <WorkIcon />
                </IconBox>
                <Typography variant="h6" sx={{ mb: 1 }}>Employee Management</Typography>
                <Typography variant="body2" color="textSecondary">
                  Manage employees, view status, and track document compliance.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateToDashboard(ROUTES.CUSTOMER.EMPLOYEES.LIST);
                  }}
                >
                  View Dashboard
                </Button>
                <Chip 
                  label={`${dashboardData.activeEmployees} employees`} 
                  size="small" 
                  color="primary"
                />
              </Box>
            </DashboardCard>
          </Grid>
          
          {/* Workflow Requests */}
          <Grid item xs={12} sm={6} md={4}>
            <DashboardCard onClick={() => navigateToDashboard(ROUTES.CUSTOMER.WORKFLOW.REQUESTS)}>
              <Box>
                <IconBox color="#7f8c8d">
                  <DashboardIcon />
                </IconBox>
                <Typography variant="h6" sx={{ mb: 1 }}>Workflow Requests</Typography>
                <Typography variant="body2" color="textSecondary">
                  Manage service requests, view request status, and track service delivery.
                </Typography>
              </Box>
              <Button 
                variant="outlined" 
                size="small" 
                sx={{ mt: 2, alignSelf: 'flex-start' }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigateToDashboard(ROUTES.CUSTOMER.WORKFLOW.REQUESTS);
                }}
              >
                View Requests
              </Button>
            </DashboardCard>
          </Grid>
        </Grid>
      </DashboardSection>
      
      {/* Quick Actions */}
      <DashboardSection>
        <Typography variant="h6" sx={{ mb: 2, color: '#064d51' }}>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="contained" 
              fullWidth 
              onClick={() => navigateToDashboard(ROUTES.CUSTOMER.WORKFLOW.NEW_REQUEST)}
              sx={{ py: 1.5, backgroundColor: '#064d51' }}
            >
              Create New Request
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="outlined" 
              fullWidth 
              onClick={() => navigateToDashboard('/ticket/my-tickets')}
              sx={{ py: 1.5, borderColor: '#064d51', color: '#064d51' }}
            >
              View Active Tickets
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="outlined" 
              fullWidth 
              onClick={() => navigateToDashboard('/CST-Dashboard/financial')}
              sx={{ py: 1.5, borderColor: '#27ae60', color: '#27ae60' }}
            >
              View Financial Status
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="outlined" 
              fullWidth 
              onClick={() => navigateToDashboard('/CST-Dashboard/documents')}
              sx={{ py: 1.5, borderColor: '#3498db', color: '#3498db' }}
            >
              Check Document Status
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigateToDashboard(ROUTES.CUSTOMER.DASHBOARD.REPORT_EMAIL)}
              sx={{ py: 1.5, backgroundColor: '#1976d2' }}
            >
              Send Document Report Email
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigateToDashboard(ROUTES.CUSTOMER.DASHBOARD.REPORTS)}
              sx={{ py: 1.5, borderColor: '#064d51', color: '#064d51' }}
            >
              Document Reports
            </Button>
          </Grid>
        </Grid>
      </DashboardSection>
    </DashboardContainer>
  );
};

export default CustomerAdminDashboard;